/**
 * What a theme is (docs/design/theming.md): a look (semantic tokens only) and default
 * layout features. Components never read a theme object or its id: they use the CSS
 * variables the provider derives from it (`var(--color-accent)`).
 *
 * Adding a theme = one file in this folder exporting a `Theme` + one line in
 * `./index.ts`. The schema below and the contrast test in packages/ui decide whether
 * it can be merged.
 */
import { z } from 'zod';

/** Every colour a theme must define. Names are roles, never hues. */
export const COLOR_TOKENS = [
  'bg',
  'surface',
  'surfaceRaised',
  'surfaceSunken',
  /** Decorative dividers (not a component boundary, so no contrast requirement). */
  'border',
  /** Boundaries of inputs and other controls: 3:1 against bg and surface. */
  'borderStrong',
  'text',
  'textMuted',
  'accent',
  'accentHover',
  'onAccent',
  'accentSoft',
  'onAccentSoft',
  'focus',
  'success',
  'onSuccess',
  'warning',
  'onWarning',
  'danger',
  'onDanger',
  'info',
  'onInfo',
  'veg',
  'nonVeg',
  'overlay',
] as const;
export type ColorToken = (typeof COLOR_TOKENS)[number];

/** Table states on the table map (POS, captain). */
export const TABLE_STATES = ['free', 'occupied', 'billed', 'reserved'] as const;
export type TableState = (typeof TABLE_STATES)[number];

/** Size of the category palette (tiles tinted by category). */
export const CATEGORY_COUNT = 8;

export const FONT_SIZES = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'display'] as const;
export const RADII = ['sm', 'md', 'lg', 'pill'] as const;
export const SHADOWS = ['sm', 'md', 'lg'] as const;

const colour = z.string().regex(/^#[0-9a-fA-F]{6}$/, { message: 'Expected #rrggbb' });
/** Overlays may be translucent. */
const colourWithAlpha = z.string().regex(/^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/, {
  message: 'Expected #rrggbb or #rrggbbaa',
});
const pair = z.strictObject({ bg: colour, fg: colour });

export const colorsSchema = z.strictObject({
  ...(Object.fromEntries(
    COLOR_TOKENS.map((t) => [t, t === 'overlay' ? colourWithAlpha : colour]),
  ) as Record<ColorToken, typeof colour>),
  table: z.strictObject(
    Object.fromEntries(TABLE_STATES.map((s) => [s, pair])) as Record<TableState, typeof pair>,
  ),
  /** 8 category colours, each with the text colour used on it. */
  categories: z.array(pair).length(CATEGORY_COUNT),
});
export type ThemeColors = z.output<typeof colorsSchema>;

const cssLength = z
  .string()
  .regex(/^\d+(\.\d+)?(rem|px|em)$/, { message: 'Expected a CSS length' });

export const typographySchema = z.strictObject({
  fontSans: z.string().min(1),
  /** Prices, quantities, timers: tabular figures. */
  fontNumeric: z.string().min(1),
  sizes: z.strictObject(
    Object.fromEntries(FONT_SIZES.map((s) => [s, cssLength])) as Record<
      (typeof FONT_SIZES)[number],
      typeof cssLength
    >,
  ),
  lineHeight: z.number().min(1).max(2),
  weights: z.strictObject({
    regular: z.int().min(100).max(900),
    medium: z.int().min(100).max(900),
    bold: z.int().min(100).max(900),
  }),
});

export const lookSchema = z.strictObject({
  colorScheme: z.enum(['light', 'dark']),
  colors: colorsSchema,
  shadows: z.strictObject(
    Object.fromEntries(SHADOWS.map((s) => [s, z.string().min(1)])) as Record<
      (typeof SHADOWS)[number],
      z.ZodString
    >,
  ),
});
export type ThemeLook = z.output<typeof lookSchema>;

export const DENSITIES = ['comfortable', 'compact'] as const;
export type Density = (typeof DENSITIES)[number];

/** Layout features: behaviour switches that work in every theme. */
export const featuresSchema = z.strictObject({
  /** Type a short item code + Enter to add an item. */
  itemCodeEntry: z.boolean(),
  /** Open tables across the top bar. */
  tableStrip: z.boolean(),
  /** Item tiles tinted by category. */
  categoryColours: z.boolean(),
  density: z.enum(DENSITIES),
  /** F-keys on tender buttons. */
  keyboardShortcuts: z.boolean(),
});
export type LayoutFeatures = z.output<typeof featuresSchema>;
export const FEATURE_KEYS = Object.keys(featuresSchema.shape) as (keyof LayoutFeatures)[];

export const themeSchema = z
  .strictObject({
    /** kebab-case, stable: stored in preferences and `outlet_setting` (`ui.theme`). */
    id: z.string().regex(/^[a-z][a-z0-9-]{1,39}$/),
    name: z.string().min(1).max(40),
    description: z.string().min(1).max(160),
    /** The theme's own look. */
    look: lookSchema,
    /**
     * Optional look for the other colour scheme (e.g. a dark variant of a light
     * theme). When present, the system's light/dark preference picks between them.
     */
    alternate: lookSchema.optional(),
    typography: typographySchema,
    radii: z.strictObject(
      Object.fromEntries(RADII.map((r) => [r, cssLength])) as Record<
        (typeof RADII)[number],
        typeof cssLength
      >,
    ),
    /** Base spacing step in px; the spacing scale is multiples of it. */
    space: z.int().min(2).max(8),
    /** Minimum touch target in px (WCAG 2.5.5 asks 44). */
    touchMin: z.int().min(44).max(96),
    features: featuresSchema,
  })
  .refine((t) => t.alternate === undefined || t.alternate.colorScheme !== t.look.colorScheme, {
    message: 'The alternate look must use the other colour scheme',
    path: ['alternate', 'colorScheme'],
  });

export type Theme = z.output<typeof themeSchema>;

/** Identity helper for theme files: keeps literal checking and documents intent. */
export function defineTheme(theme: Theme): Theme {
  return theme;
}
